import { useCallback } from "react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import type { AttentionBannerKind } from "./attention-banner";
import type { AttentionHeaderActionId } from "./attention-header";
import {
  attentionBannerActionProps,
  attentionBannerProps,
  attentionBannerToggleProps,
  attentionHeaderActionProps,
  attentionMessageSendProps,
  attentionSuggestionProps,
  attentionToolbarActionProps,
  attentionVoiceProps,
  type AttentionMessageType,
  type AttentionSuggestionType,
  type AttentionToolbarAction,
} from "./attention-analytics";

// Payloads flow through the pure builders in `attention-analytics.ts`, so nothing
// here carries message text, names, GIF URLs, or suggestion content.
export function useAttentionAnalytics(teenId: string | null) {
  const { track } = useTrackEvent();

  const trackBannerView = useCallback(
    (kind: AttentionBannerKind) =>
      track(PostHogEvents.ATTENTION_BANNER_VIEW, attentionBannerProps(kind, teenId)),
    [track, teenId],
  );

  const trackBannerAction = useCallback(
    (kind: AttentionBannerKind, actionId: string) =>
      track(
        PostHogEvents.ATTENTION_BANNER_CLICK,
        attentionBannerActionProps(kind, actionId, teenId),
      ),
    [track, teenId],
  );

  const trackBannerDismiss = useCallback(
    (kind: AttentionBannerKind) =>
      track(
        PostHogEvents.ATTENTION_BANNER_DISMISS,
        attentionBannerProps(kind, teenId),
      ),
    [track, teenId],
  );

  const trackBannerToggle = useCallback(
    (kind: AttentionBannerKind, open: boolean) =>
      track(
        open
          ? PostHogEvents.ATTENTION_BANNER_EXPAND
          : PostHogEvents.ATTENTION_BANNER_COLLAPSE,
        attentionBannerToggleProps(kind, open ? "expanded" : "collapsed", teenId),
      ),
    [track, teenId],
  );

  const trackToolbarAction = useCallback(
    (action: AttentionToolbarAction) =>
      track(
        PostHogEvents.ATTENTION_COMPOSER_TOOLBAR_ACTION,
        attentionToolbarActionProps(action, teenId),
      ),
    [track, teenId],
  );

  const trackMessageSend = useCallback(
    (messageType: AttentionMessageType, hasQuote: boolean) =>
      track(
        PostHogEvents.ATTENTION_MESSAGE_SEND,
        attentionMessageSendProps(messageType, hasQuote, teenId),
      ),
    [track, teenId],
  );

  const trackVoiceSend = useCallback(
    (durationMs: number, hasQuote: boolean) =>
      track(
        PostHogEvents.ATTENTION_VOICE_RECORD_SEND,
        attentionVoiceProps(durationMs, hasQuote, teenId),
      ),
    [track, teenId],
  );

  const trackVoiceCancel = useCallback(
    (durationMs: number) =>
      track(
        PostHogEvents.ATTENTION_VOICE_RECORD_CANCEL,
        attentionVoiceProps(durationMs, false, teenId),
      ),
    [track, teenId],
  );

  const trackSuggestionOpen = useCallback(
    () =>
      track(
        PostHogEvents.ATTENTION_SUGGESTION_OPEN,
        attentionSuggestionProps(null, null, teenId),
      ),
    [track, teenId],
  );

  const trackSuggestionInsert = useCallback(
    (suggestionType: AttentionSuggestionType | null, approach: string | null) =>
      track(
        PostHogEvents.ATTENTION_SUGGESTION_INSERT,
        attentionSuggestionProps(suggestionType, approach, teenId),
      ),
    [track, teenId],
  );

  const trackSuggestionRegenerate = useCallback(
    (suggestionType: AttentionSuggestionType | null, approach: string | null) =>
      track(
        PostHogEvents.ATTENTION_SUGGESTION_REGENERATE,
        attentionSuggestionProps(suggestionType, approach, teenId),
      ),
    [track, teenId],
  );

  const trackSuggestionDismiss = useCallback(
    (suggestionType: AttentionSuggestionType | null) =>
      track(
        PostHogEvents.ATTENTION_SUGGESTION_DISMISS,
        attentionSuggestionProps(suggestionType, null, teenId),
      ),
    [track, teenId],
  );

  const trackHeaderAction = useCallback(
    (actionId: AttentionHeaderActionId) =>
      track(
        PostHogEvents.ATTENTION_HEADER_ACTION,
        attentionHeaderActionProps(actionId, teenId),
      ),
    [track, teenId],
  );

  return {
    trackBannerView,
    trackBannerAction,
    trackBannerDismiss,
    trackBannerToggle,
    trackToolbarAction,
    trackMessageSend,
    trackVoiceSend,
    trackVoiceCancel,
    trackSuggestionOpen,
    trackSuggestionInsert,
    trackSuggestionRegenerate,
    trackSuggestionDismiss,
    trackHeaderAction,
  };
}

export type UseAttentionAnalyticsResult = ReturnType<typeof useAttentionAnalytics>;
